import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import MoleculeLoader from '../components/MoleculeLoader';
import { CheckCircle2, Mail, Package, Ban } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreferencesPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [news, setNews] = useState(false);
  const [inv, setInv] = useState(false);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (!email || !token) {
      setError('Invalid preferences link.');
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/api/communication-preferences?email=${encodeURIComponent(email)}&token=${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Invalid link');
        setName(d.name || '');
        setNews(!!d.newsletter_unsubscribed);
        setInv(!!d.inventory_notifications_unsubscribed);
        setAll(!!d.all_communications_unsubscribed);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [email, token]);

  const save = async (payload) => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/communication-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, ...payload }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to save');
      setNews(!!d.newsletter_unsubscribed);
      setInv(!!d.inventory_notifications_unsubscribed);
      setAll(!!d.all_communications_unsubscribed);
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (which, value) => {
    setSaved(false);
    let next = { newsletter_unsubscribed: news, inventory_notifications_unsubscribed: inv, all_communications_unsubscribed: all };
    if (which === 'all') {
      next = { newsletter_unsubscribed: value, inventory_notifications_unsubscribed: value, all_communications_unsubscribed: value };
      setAll(value); setNews(value); setInv(value);
    } else if (which === 'news') { next.newsletter_unsubscribed = value; next.all_communications_unsubscribed = false; setNews(value); setAll(false); }
    else if (which === 'inv') { next.inventory_notifications_unsubscribed = value; next.all_communications_unsubscribed = false; setInv(value); setAll(false); }
    save(next);
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><MoleculeLoader /></div>;
  }

  return (
    <div className="min-h-[70vh] bg-slate-50 py-16 px-4" data-testid="preferences-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <h1 className="text-2xl font-bold text-slate-900">Email Preferences</h1>
        <p className="text-sm text-slate-500 mt-1">
          {email ? <>Managing preferences for <strong className="text-slate-700">{email}</strong>{name ? ` (${name})` : ''}</> : 'Manage what you hear from Genora Peptides.'}
        </p>

        {error ? (
          <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="preferences-error">{error}</div>
        ) : (
          <div className="mt-6 space-y-4">
            <PrefRow
              icon={Package} title="Inventory & restock alerts"
              desc="New arrivals and back-in-stock emails."
              checkedOff={inv} disabled={saving || all}
              testid="pref-inventory"
              onChange={(v) => onToggle('inv', v)}
            />
            <PrefRow
              icon={Mail} title="Newsletter & articles"
              desc="Research insights, guides and occasional promos."
              checkedOff={news} disabled={saving || all}
              testid="pref-newsletter"
              onChange={(v) => onToggle('news', v)}
            />
            <div className="pt-2 border-t border-slate-100">
              <PrefRow
                icon={Ban} title="Opt out of ALL communication"
                desc="Stop every marketing email from Genora Peptides."
                checkedOff={all} disabled={saving}
                testid="pref-all" danger
                onChange={(v) => onToggle('all', v)}
              />
            </div>

            {saved && (
              <div className="flex items-center gap-2 text-teal-700 text-sm mt-2" data-testid="preferences-saved">
                <CheckCircle2 className="h-4 w-4" /> Preferences saved.
              </div>
            )}
            <p className="text-xs text-slate-400 mt-4">
              You can change these anytime. Transactional emails about your orders are always sent.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Toggle is "on" when the customer is SUBSCRIBED; checkedOff means they've opted out.
const PrefRow = ({ icon: Icon, title, desc, checkedOff, disabled, onChange, testid, danger }) => (
  <div className={`flex items-start justify-between gap-4 p-4 rounded-xl border ${danger ? 'border-red-200 bg-red-50/40' : 'border-slate-200'}`}>
    <div className="flex gap-3">
      <Icon className={`h-5 w-5 mt-0.5 ${danger ? 'text-red-500' : 'text-teal-600'}`} />
      <div>
        <div className="font-semibold text-slate-800 text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        <div className={`text-xs mt-1 font-medium ${checkedOff ? 'text-red-600' : 'text-teal-600'}`}>
          {checkedOff ? 'Opted out' : 'Subscribed'}
        </div>
      </div>
    </div>
    <Switch checked={checkedOff} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default PreferencesPage;
